import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MAIN_URL } from '../../Configs/Urls';
import axios from '../../Configs/axiosInstance';
import { Alert, Chip, Paper, Snackbar, Stack, Typography, Box, Avatar, Button, CircularProgress, Divider } from '@mui/material';
import { format } from 'date-fns';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import MonitorWeightRoundedIcon from '@mui/icons-material/MonitorWeightRounded';
import RestaurantRoundedIcon from '@mui/icons-material/RestaurantRounded';
import MedicalServicesRoundedIcon from '@mui/icons-material/MedicalServicesRounded';
import { Section } from './Components/PatientFormPieces';
import { colorForName, initialsFor } from './Components/avatarUtils';

// One label/value row used throughout the read-only sections below.
function InfoRow({ label, value }) {
    return (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 190, fontWeight: 600 }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ flex: 1 }}>
                {value || <span style={{ opacity: 0.5 }}>—</span>}
            </Typography>
        </Box>
    );
}

function ChipListRow({ label, values }) {
    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                {label}
            </Typography>
            {values && values.length > 0 ? (
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    {values.map((value) => (
                        <Chip key={value} label={value} size="small" variant="outlined" />
                    ))}
                </Stack>
            ) : (
                <Typography variant="body2" sx={{ opacity: 0.5 }}>—</Typography>
            )}
        </Box>
    );
}

function PatientView() {
    const { id: patientId } = useParams();
    const navigate = useNavigate();

    const [patient, setPatient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    const handleCloseSnackbar = () => setOpenSnackbar(false);

    useEffect(() => {
        if (!patientId) return;

        setLoading(true);
        axios
            .get(`${MAIN_URL}patients/${patientId}`)
            .then((response) => setPatient(response.data))
            .catch(() => {
                setSnackbarMessage("Error fetching patient data.");
                setSnackbarSeverity("error");
                setOpenSnackbar(true);
            })
            .finally(() => setLoading(false));
    }, [patientId]);

    if (loading) {
        return (
            <Box sx={{ width: "100%", display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!patient) {
        return (
            <Box sx={{ width: "100%" }}>
                <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate('/patients')} sx={{ mb: 2, textTransform: 'none' }}>
                    Back to Patients List
                </Button>
                <Typography color="text.secondary">Patient not found.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: "100%", maxWidth: 960 }}>
            <Button
                startIcon={<ArrowBackRoundedIcon />}
                onClick={() => navigate('/patients')}
                sx={{ mb: 2, textTransform: 'none', fontWeight: 600 }}
            >
                Back to Patients List
            </Button>

            <Stack spacing={3}>
                {/* Summary card */}
                <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 3.5 }, borderRadius: 3, borderColor: 'divider' }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 2 }}>
                        <Avatar sx={{ width: 64, height: 64, fontSize: '1.4rem', bgcolor: colorForName(patient.name) }}>
                            {initialsFor(patient.name)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {patient.name}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap', gap: 1 }}>
                                {patient.gender && <Chip label={patient.gender} size="small" variant="outlined" />}
                                <Typography variant="body2" color="text.secondary">{patient.age} yrs</Typography>
                                <Typography variant="body2" color="text.secondary">·</Typography>
                                <Typography variant="body2" color="text.secondary">{patient.contactNumber}</Typography>
                            </Stack>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<EditRoundedIcon />}
                            onClick={() => navigate(`/patients/${patientId}/edit`)}
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                        >
                            Edit
                        </Button>
                    </Stack>

                    <Divider sx={{ my: 2.5 }} />

                    <Stack spacing={1.25}>
                        <InfoRow label="Email" value={patient.email} />
                        <InfoRow label="Address" value={patient.address} />
                        <InfoRow
                            label="Added at"
                            value={patient.created_at ? format(new Date(patient.created_at), "MMMM do, yyyy 'at' h:mm a") : null}
                        />
                        <InfoRow
                            label="Last updated"
                            value={patient.updated_at ? format(new Date(patient.updated_at), "MMMM do, yyyy 'at' h:mm a") : null}
                        />
                    </Stack>
                </Paper>

                <Section icon={<MonitorWeightRoundedIcon />} title="Physical Measurements">
                    <InfoRow label="Height" value={patient.height ? `${patient.height} ft` : null} />
                    <InfoRow label="Weight" value={patient.weight ? `${patient.weight} kg` : null} />
                </Section>

                <Section icon={<RestaurantRoundedIcon />} title="Dietary & Lifestyle">
                    <InfoRow label="Dietary Preference" value={patient.dietaryPreference} />
                    <InfoRow label="Alcohol Consumption" value={patient.alcohol} />
                    <ChipListRow label="Exercise Regime" values={patient.exerciseRegime} />
                    <InfoRow label="Lifestyle" value={patient.lifestyle} />
                    <InfoRow label="Eating Pattern" value={patient.eatingPattern} />
                    <InfoRow label="Job Specifications" value={patient.jobSpecifications} />
                </Section>

                <Section icon={<MedicalServicesRoundedIcon />} title="Medical History">
                    <ChipListRow label="Physiological Conditions" values={patient.physiologicalConditions} />
                    {patient.otherPhysiologicalCondition && (
                        <InfoRow label="Other Condition" value={patient.otherPhysiologicalCondition} />
                    )}
                    <InfoRow label="Food Allergy" value={patient.foodAllergy} />
                    <InfoRow label="Family History" value={patient.familyHistory} />
                    <InfoRow label="Surgery" value={patient.surgery} />
                    <InfoRow label="Medications" value={patient.medications} />
                    <InfoRow label="Supplements" value={patient.supplements} />
                </Section>
            </Stack>

            <Snackbar
                open={openSnackbar}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbarSeverity}
                    variant="filled"
                    sx={{ fontWeight: 600 }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default PatientView;
